import { Anchor, Badge, Button, Code, Flex, Grid, Table, Text } from "@mantine/core";
import { useApiUrl, useCustomMutation, useShow } from "@refinedev/core";
import { DateField, EditButton, ListButton, RefreshButton, Show } from "@refinedev/mantine";
import {
  DetailsCard,
  DetailsUserCard,
} from "components/common/show/DetailsPair";
import { History } from "components/common/show/History";
import { TeaAddonOperations } from "components/tea_addon_renewal_runs/TeaAddonOperations";
import { TeaAddonSubscription } from "entities/tea_addon_subscription";
import { colorsByStatus } from "entities/tea_addon_subscription/status";
import { Link, useNavigate, useParams } from "react-router-dom";
import Timeline from "./Timeline";
import { IconRefresh } from "@tabler/icons-react";

type OperationResponse = {
  success: boolean;
  error?: string;
};

export const TeaAddonSubscriptionShow = () => {
  const params = useParams();

  const { queryResult } = useShow<TeaAddonSubscription>();
  const { data, isLoading, refetch } = queryResult;
  const tea_addon_subscription = data?.data;

  const apiUrl = useApiUrl();

  const { mutate: syncToOMS, isLoading: isSyncingToOMS } =
      useCustomMutation<OperationResponse>();

  if (!tea_addon_subscription) return <div />;

  return (
    <Show
    isLoading={isLoading}
    headerButtons={
            <Flex gap="sm">
              <ListButton />
              <EditButton />
              <RefreshButton />
              <Button
                leftIcon={<IconRefresh />}
                color="blue"
                disabled={isSyncingToOMS}
                onClick={() =>
                  tea_addon_subscription &&
                  syncToOMS(
                    {
                      url: `${apiUrl}/tea_addon_subscriptions/${tea_addon_subscription.id}/sync_to_oms`,
                      method: "post",
                      values: {},
                    },
                    {
                      onSuccess: () => refetch(),
                      onError: () => refetch(),
                    },
                  )
                }
              >
                Update order's status & tags
              </Button>
            </Flex>
          }
    >
      <Grid>
        <Grid.Col span={8}>
          <Flex gap="sm" direction="column">
            <DetailsCard title="Timeline">
              {params.id && <Timeline id={params.id} />}
            </DetailsCard>
            <DetailsCard title="Tea Addons">
              <Table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Box Name</th>
                    <th>ChargeBee Addon ID</th>
                    <th>Shippable At</th>
                    <th>Invoice</th>
                    <th>Order Invoice</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {tea_addon_subscription.tea_addons.map((tea_addon) => (
                    <tr>
                      <td>
                        <Anchor
                          component={Link}
                          to={`/tea_addons/show/${tea_addon.id}`}
                        >
                          {tea_addon.id}
                        </Anchor>
                      </td>
                      <td>
                        <Text weight="bold">{tea_addon.name}</Text>
                      </td>
                      <td>
                        <Badge color="indigo">{tea_addon.box_name}</Badge>
                      </td>
                      <td>
                        <Code>{tea_addon.chargebee_addon_id}</Code>
                      </td>
                      <td>
                        <DateField
                          value={tea_addon.shippable_at}
                          format="DD/MM/YYYY"
                        />
                      </td>
                      <td>
                        <Anchor
                          component={Link}
                          to={`/invoices/show/${tea_addon.invoice?.id}`}
                        >
                          {tea_addon.invoice?.remote_id}
                        </Anchor>
                      </td>
                      <td>
                        <Anchor
                          component={Link}
                          to={`/invoices/show/${tea_addon.order_invoice?.id}`}
                        >
                          {tea_addon.order_invoice?.remote_id}
                        </Anchor>
                      </td>
                      <td>
                        <DateField
                          value={tea_addon.created_at}
                          format="DD/MM/YYYY HH:mm:ss"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </DetailsCard>
            <DetailsCard title="Operations">
              <TeaAddonOperations
                tea_addon_operations={
                  tea_addon_subscription.tea_addon_operations
                }
                onOperation={queryResult.refetch}
                showRenewalRun
              />
            </DetailsCard>
            <DetailsCard title="Notes">
              {tea_addon_subscription.notes}
            </DetailsCard>
            <History<TeaAddonSubscription>
              resources={"tea_addon_subscriptions"}
              entityId={tea_addon_subscription.id}
            />
          </Flex>
        </Grid.Col>
        <Grid.Col span={4}>
          <Flex direction="column" gap="sm">
            <DetailsUserCard user={tea_addon_subscription.user} />
            <DetailsCard title="ID">
              <Text>{tea_addon_subscription.id}</Text>
            </DetailsCard>
            <DetailsCard title="Subscription">
              <Anchor
                component={Link}
                to={`/subscriptions/show/${tea_addon_subscription.subscription?.id}`}
              >
                {tea_addon_subscription.subscription?.remote_id}
              </Anchor>
            </DetailsCard>
            <DetailsCard title="Status">
              <Badge color={colorsByStatus[tea_addon_subscription.status]}>
                {tea_addon_subscription.status}
              </Badge>
            </DetailsCard>
            <DetailsCard title="Last Payment At">
              <Code>{tea_addon_subscription.last_payment_at?.toString()}</Code>
            </DetailsCard>
            <DetailsCard title="Next Renewal At">
              <Code>{tea_addon_subscription.next_renewal_at?.toString()}</Code>
            </DetailsCard>
            <DetailsCard title="Canceled At">
              <Code>{tea_addon_subscription.canceled_at?.toString()}</Code>
            </DetailsCard>
          </Flex>
        </Grid.Col>
      </Grid>
    </Show>
  );
};
